import { Injectable, signal, computed } from '@angular/core';
import type { CommonPortfolio, CommonPortfolioSearchResult } from './commonportfolio.service';

const STORAGE_KEY = 'selectedPortfolio';
const PORTFOLIO_IN_GROUP_KEY = 'selectedPortfolioInGroupId';

/**
 * Global portfolio state shared across all views.
 * Persists to sessionStorage so selection survives page navigation.
 */
@Injectable({ providedIn: 'root' })
export class PortfolioStateService {
  private readonly _portfolio = signal<CommonPortfolioSearchResult | null>(this.loadFromStorage());
  private readonly _reloadTrigger = signal(0);
  private readonly _selectedPortfolioInGroupId = signal<string | null>(this.loadPortfolioInGroupFromStorage());
  private readonly _selectedPortfolioInGroupObj = signal<CommonPortfolio | null>(null);
  private readonly _accessDenied = signal(false);

  /** Current selected portfolio group (reactive) */
  readonly portfolio = this._portfolio.asReadonly();

  /** Increment to signal subscribers (e.g. properties list) to reload their data */
  readonly reloadTrigger = this._reloadTrigger.asReadonly();

  /** Individual portfolio selected within the group, or null for whole group */
  readonly selectedPortfolioInGroupId = this._selectedPortfolioInGroupId.asReadonly();

  /** Full portfolio object for the individual portfolio selected within the group */
  readonly selectedPortfolioInGroup = this._selectedPortfolioInGroupObj.asReadonly();

  /** True when the current portfolio returned 403 — user lacks access */
  readonly accessDenied = this._accessDenied.asReadonly();

  setAccessDenied(denied: boolean): void {
    this._accessDenied.set(denied);
  }

  triggerReload(): void {
    this._reloadTrigger.update(v => v + 1);
  }

  /**
   * Convenience: current portfolio ID or null.
   *
   * This is the FIRST PORTFOLIO in the selected group, not the group. The dashboard builds this
   * object from `getPortfoliosByGroup(...)[0]`. Use it only where a portfolio is genuinely wanted —
   * the bulk upload target, and source-property lookups. For routing or any group-scoped API call
   * use `portfolioGroupId` below.
   */
  readonly portfolioId = computed(() => this._portfolio()?.portfolioId ?? null);

  /**
   * Convenience: current portfolio GROUP ID or null. This is what `/dashboard/:portfolioId/...`
   * carries, despite the route parameter's name.
   *
   * Not interchangeable with `portfolioId`. Both are plain numbers and the two ID spaces overlap on
   * 103 values in UAT, each pointing at a different client, so transposing them scopes to the wrong
   * client silently instead of failing — 346403.
   */
  readonly portfolioGroupId = computed(() => this._portfolio()?.portfolioGroupId ?? null);

  /** Convenience: current portfolio name or null */
  readonly portfolioName = computed(() => this._portfolio()?.name ?? null);

  select(portfolio: CommonPortfolioSearchResult): void {
    this._portfolio.set(portfolio);
    this._accessDenied.set(false);
    this.saveToStorage(portfolio);
  }

  selectPortfolioInGroup(portfolioId: string | null, portfolio: CommonPortfolio | null = null): void {
    this._selectedPortfolioInGroupId.set(portfolioId);
    this._selectedPortfolioInGroupObj.set(portfolio);
    try {
      if (portfolioId) {
        sessionStorage.setItem(PORTFOLIO_IN_GROUP_KEY, portfolioId);
      } else {
        sessionStorage.removeItem(PORTFOLIO_IN_GROUP_KEY);
      }
    } catch { /* ignore */ }
  }

  clear(): void {
    this._portfolio.set(null);
    this._selectedPortfolioInGroupId.set(null);
    this._selectedPortfolioInGroupObj.set(null);
    this._accessDenied.set(false);
    try {
      sessionStorage.removeItem(STORAGE_KEY);
      sessionStorage.removeItem(PORTFOLIO_IN_GROUP_KEY);
    } catch { /* ignore */ }
  }

  private loadFromStorage(): CommonPortfolioSearchResult | null {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  }

  private loadPortfolioInGroupFromStorage(): string | null {
    try {
      return sessionStorage.getItem(PORTFOLIO_IN_GROUP_KEY);
    } catch {
      return null;
    }
  }

  private saveToStorage(portfolio: CommonPortfolioSearchResult): void {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(portfolio));
    } catch { /* ignore */ }
  }
}
